/**
 * SickLeaveDayPanel.js
 *
 * Side panel showing all sick leave entries for a selected day.
 * Delete-only — there is no update operation for sick leave,
 * so no edit button or edit modal is present.
 *
 * Mirrors: VacationDayPanel.js
 * Differences:
 * - No edit action, no edit modal
 * - No holiday prop or holiday display
 * - Uses sickLeave namespace for all strings
 * - Receives `sickLeave` array prop instead of `vacations`
 */

import React, { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSickLeave } from '../../contexts/SickLeaveContext';
import { formatDurationFromMinutes } from '../../utils/TimeSlotUtils';

const SickLeaveDayPanel = ({
  isOpen,
  onClose,
  date,
  sickLeave = [],
  staff = [],
  departments = []
}) => {
  const { t, i18n } = useTranslation('sickLeave');
  const { deleteSickLeave, loading: contextLoading } = useSickLeave();

  const [deletingId, setDeletingId] = useState(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  /**
   * Staff lookup map — O(1) fallback resolution.
   */
  const staffMap = useMemo(() => {
    const map = new Map();
    staff.forEach(s => map.set(s.id, s));
    return map;
  }, [staff]);

  /**
   * Department lookup map — O(1) fallback resolution.
   */
  const departmentMap = useMemo(() => {
    const map = new Map();
    departments.forEach(d => map.set(d.id, d));
    return map;
  }, [departments]);

  /**
   * Resolve staff name — prefers enriched data set by the context.
   */
  const getStaffName = useCallback((entry) => {
    if (entry.staffName && entry.staffName !== 'Lädt...') return entry.staffName;
    return staffMap.get(entry.staffId)?.name ||
      t('unknownStaff', { defaultValue: 'Unknown Staff' });
  }, [staffMap, t]);

  /**
   * Resolve department name — prefers enriched data set by the context.
   */
  const getDepartmentName = useCallback((entry) => {
    if (entry.departmentName && entry.departmentName !== 'Lädt...') return entry.departmentName;
    return departmentMap.get(entry.departmentId)?.name ||
      t('unknownDepartment', { defaultValue: 'Unknown Department' });
  }, [departmentMap, t]);

  /**
   * Localised long-form date for the panel header.
   */
  const formattedDate = useMemo(() => {
    if (!date) return '';
    return date.toLocaleDateString(i18n.language, {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  }, [date, i18n.language]);

  /**
   * Sum of all entry durations for the footer total.
   */
  const totalMinutes = useMemo(() => {
    return sickLeave.reduce((sum, entry) => sum + (entry.durationMinutes || 0), 0);
  }, [sickLeave]);

  const handleDeleteClick = useCallback((id) => {
    setDeletingId(id);
    setShowConfirmDelete(true);
  }, []);

  const handleConfirmDelete = async () => {
    if (!deletingId) return;

    setIsDeleting(true);
    try {
      await deleteSickLeave(deletingId);
      setShowConfirmDelete(false);
      setDeletingId(null);
      // Close panel when the last entry on this day is deleted
      if (sickLeave.length <= 1) onClose();
    } catch (err) {
      console.error('Error deleting sick leave:', err);
      alert(t('deleteError') + (err.message ? ': ' + err.message : ''));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = useCallback(() => {
    setShowConfirmDelete(false);
    setDeletingId(null);
  }, []);

  const handleOverlayClick = useCallback((e) => {
    if (e.target.classList.contains('panel-overlay')) onClose();
  }, [onClose]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape' && !showConfirmDelete) onClose();
  }, [onClose, showConfirmDelete]);

  React.useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="panel-overlay"
        onClick={handleOverlayClick}
        aria-hidden="true"
      />

      {/* Side Panel */}
      <div
        className={`vacation-day-panel ${isOpen ? 'open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="sl-panel-title"
      >

        {/* Header */}
        <div className="panel-header">
          <div className="panel-title">
            <h2 id="sl-panel-title">{formattedDate}</h2>
            <p className="panel-subtitle">
              {sickLeave.length} {t('sickDays')}
            </p>
          </div>
          <button
            className="btn-close"
            onClick={onClose}
            aria-label={t('close', { ns: 'common', defaultValue: 'Close' })}
            title={t('closeEsc', { defaultValue: 'Close (Esc)' })}
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="panel-content">
          {sickLeave.length === 0 ? (
            <div className="empty-message">
              <p>{t('noSickLeave')}</p>
            </div>
          ) : (
            <div className="vacation-list">
              {sickLeave.map((entry) => {
                const staffName = getStaffName(entry);
                const departmentName = getDepartmentName(entry);
                const isLoadingData =
                  entry.staffName === 'Lädt...' ||
                  entry.departmentName === 'Lädt...';
                const durationDisplay = formatDurationFromMinutes(
                  entry.durationMinutes || 0
                );

                return (
                  <div
                    key={entry.id}
                    className={`vacation-item ${isLoadingData ? 'loading' : ''}`}
                  >
                    <div className="vacation-info">
                      <div className="staff-name">
                        {isLoadingData ? (
                          <span className="loading-text">
                            {t('loading', { defaultValue: 'Loading...' })}
                          </span>
                        ) : (
                          staffName
                        )}
                      </div>
                      <div className="vacation-details">
                        <span className="department-name">
                          {isLoadingData
                            ? t('loading', { defaultValue: 'Loading...' })
                            : departmentName}
                        </span>
                        <span
                          className="hours-badge"
                          title={`${t('duration')}: ${durationDisplay}`}
                        >
                          {durationDisplay}
                        </span>
                      </div>
                    </div>

                    <div className="vacation-actions">
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDeleteClick(entry.id)}
                        disabled={isLoadingData || contextLoading}
                        aria-label={`${t('deleteSickDay')}: ${staffName}`}
                      >
                        {t('deleteSickDay')}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer — total duration */}
        {sickLeave.length > 0 && (
          <div className="panel-footer">
            <div className="total-hours">
              <span className="label">{t('duration')}:</span>
              <span className="value">
                {formatDurationFromMinutes(totalMinutes)}
              </span>
            </div>
          </div>
        )}

      </div>

      {/* Delete Confirmation Modal */}
      {showConfirmDelete && (
        <div
          className="modal-overlay"
          onClick={handleCancelDelete}
          role="dialog"
          aria-modal="true"
          aria-labelledby="sl-delete-title"
        >
          <div
            className="confirmation-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 id="sl-delete-title">{t('deleteSickDay')}</h3>
            </div>
            <div className="modal-body">
              <p>{t('confirmDelete')}</p>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={handleCancelDelete}
                disabled={isDeleting}
              >
                {t('cancel', { ns: 'common', defaultValue: 'Cancel' })}
              </button>
              <button
                className="btn btn-danger"
                onClick={handleConfirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <span className="spinner-small"></span>
                    {t('deleting', { defaultValue: 'Deleting...' })}
                  </>
                ) : (
                  t('deleteSickDay')
                )}
              </button>
            </div>
          </div>
        </div>
      )}

    </>
  );
};

export default SickLeaveDayPanel;